import React, { useEffect, useState } from "react";

import {
  Box,
  Typography,
  Paper,
  Chip,
  CircularProgress
} from "@mui/material";

import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from "recharts";

import { getBoardOverview } from "../../services/reportService";
import uiConfig from "../../config/uiConfig";

const PALETTE = uiConfig.chartPalette;
const STATUS_COLORS = { "To Do": PALETTE[5], "In Progress": PALETTE[1], "Done": PALETTE[4] };
const TYPE_COLORS = { Task: PALETTE[0], Bug: PALETTE[3], Story: PALETTE[4] };
const PRIORITY_COLORS = { Low: PALETTE[4], Medium: PALETTE[2], High: PALETTE[3], Critical: PALETTE[3] };

const hashString = (value) => {
  let hash = 0;
  for (let i = 0; i < value.length; i += 1) {
    hash = (hash * 31 + value.charCodeAt(i)) | 0;
  }
  return Math.abs(hash);
};

// Custom project workflow statuses won't be in STATUS_COLORS - fall back to a
// deterministic pick from the palette so each name still gets a stable color.
const statusColor = (name) => STATUS_COLORS[name] || PALETTE[hashString(name) % PALETTE.length];

function MiniPie({ title, data, colors }) {
  return (
    <Box>
      <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: "text.secondary", mb: 1 }}>
        {title}
      </Typography>

      <ResponsiveContainer width="100%" height={180}>
        <PieChart>
          <Pie data={data} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={60}>
            {data.map((entry) => (
              <Cell key={entry.name} fill={colors === STATUS_COLORS ? statusColor(entry.name) : (colors[entry.name] || PALETTE[6])} />
            ))}
          </Pie>
          <Tooltip />
          <Legend wrapperStyle={{ fontSize: 11 }} />
        </PieChart>
      </ResponsiveContainer>
    </Box>
  );
}

function BoardOverviewReport({ departmentId = null }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);

    getBoardOverview(departmentId)
      .then(setData)
      .catch((error) => console.error("Board Overview Error:", error))
      .finally(() => setLoading(false));
  }, [departmentId]);

  if (loading) {
    return (
      <Box height={330} display="flex" justifyContent="center" alignItems="center">
        <CircularProgress />
      </Box>
    );
  }

  const byStatus = data?.by_status || [];
  const byType = data?.by_type || [];
  const byPriority = data?.by_priority || [];
  const sprintVelocity = data?.sprint_velocity || [];

  return (
    <Paper
      sx={{
        p: 2.5,
        borderRadius: 3,
        border: "1px solid",
        borderColor: "divider",
        boxShadow: "0 2px 8px rgba(0,0,0,0.04)"
      }}
    >
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
        <Typography sx={{ fontSize: 15, fontWeight: 700, color: "text.primary" }}>
          Sprint &amp; Task Analytics
        </Typography>

        <Chip
          size="small"
          label={`${data?.total_tasks || 0} tasks`}
          sx={{ fontWeight: 700, background: "#eef2ff", color: "#4338ca" }}
        />
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
          gap: 2,
          mb: 2.5
        }}
      >
        <MiniPie title="By Status" data={byStatus} colors={STATUS_COLORS} />
        <MiniPie title="By Type" data={byType} colors={TYPE_COLORS} />
        <MiniPie title="By Priority" data={byPriority} colors={PRIORITY_COLORS} />
      </Box>

      <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: "text.secondary", mb: 1 }}>
        Sprint Velocity (Story Points)
      </Typography>

      {sprintVelocity.length === 0 ? (
        <Typography sx={{ fontSize: 12.5, color: "text.secondary" }}>No sprints yet</Typography>
      ) : (
        <ResponsiveContainer width="100%" height={220}>
          <BarChart data={sprintVelocity}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="sprint_name" fontSize={11} />
            <YAxis allowDecimals={false} fontSize={12} />
            <Tooltip />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Bar dataKey="completed_points" name="Completed" fill={PALETTE[4]} radius={[6, 6, 0, 0]} />
            <Bar dataKey="total_points" name="Planned" fill={PALETTE[5]} radius={[6, 6, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      )}
    </Paper>
  );
}

export default BoardOverviewReport;
